import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnChanges,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Input,
  DestroyRef,
  inject,
  signal,
} from "@angular/core";
//import { MatPaginator, MatSort, MatTableDataSource } from '@angular/material';
import { MatSort } from "@angular/material/sort";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { ActivatedRoute, Router, RouterLinkWithHref } from "@angular/router";
import {
  MatDialogModule,
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogConfig,
} from "@angular/material/dialog";
import { MatMenuTrigger } from "@angular/material/menu";
import { MsalService } from "@azure/msal-angular";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";

// Services
import { StoreUserService } from '@app/core/state/store-user.service';
import { ListSectionGroupAdminsService } from "../../../data/section-group/list-section-group-admins.service";
import { DatabaseCacheService } from "../../../data/cache/database-cache.service";
import { SectionGroupAdminDetailMode } from "../section-group-admin-details/section-group-admin-details.component";

import dayjs from "dayjs";

// For Dialog view
import {
  SectionGroupAdminDetailsComponent,
  AdminData,
} from "../section-group-admin-details/section-group-admin-details.component";
import { first } from "rxjs";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { PageHeaderComponent } from "../../../shared/components/page-header/page-header.component";
import { TableSkeletonComponent } from "../../../shared/skeleton";
import { SECTION_GROUP_ADMIN_LIST_I18N } from "./section-group-admin-list.i18n";
// import { StaffRolesComponent } from '../staff-roles/staff-roles.component';
// import { StaffActivationComponent } from '../staff-activation/staff-activation.component';

@Component({
  selector: "app-section-group-admin-list",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./section-group-admin-list.component.html",
  styleUrls: ["./section-group-admin-list.component.scss"],
  imports: [...SHARED_IMPORTS, TableSkeletonComponent, PageHeaderComponent],
})
export class SectionGroupAdminListComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = SECTION_GROUP_ADMIN_LIST_I18N;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild("menuTrigger") menuTrigger: MatMenuTrigger;
  @ViewChild("searchInput") searchInput: ElementRef<HTMLInputElement>;

  loginUserId = "";
  filter = ""; //table filtering
  readonly searchExpanded = signal(false);
  isLoading = signal(true);

  sectionGroupId;
  hasAllSectionGroupManagement;
  hasSectionGroupAdminManagement;

  admins: AdminData[] = [];
  displayedColumns = ["name", "status", "assignedDate"];

  accountStatus: { [key: string]: string } = {
    OPEN: this.i18n.status.active,
    CLOSE: this.i18n.status.inactive,
  };

  //dialog with path param
  private dialogRef: MatDialogRef<any, any>;
  private routerUrl: string;

  dataSource: MatTableDataSource<AdminData>;

  constructor(
    public storeUser: StoreUserService,
    private route: ActivatedRoute,
    private listSectionGroupAdminsService: ListSectionGroupAdminsService,
    private router: Router,
    private msalService: MsalService,
    public dialog: MatDialog,
    private domSanitizer: DomSanitizer,
    private iconRegistry: MatIconRegistry,
    private common: DatabaseCacheService,
  ) {
    let user = this.msalService.instance.getActiveAccount();
    this.loginUserId = user.idTokenClaims["sub"];

    // カスタムSVG iconの呼び出し
    this.iconRegistry.addSvgIcon(
      "icon_staff", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_staff.svg",
      ), //必ずサニタイザーを利用して取得する。
    );

    this.iconRegistry.addSvgIcon(
      "icon_setting", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_setting.svg",
      ),
    );

    this.iconRegistry.addSvgIcon(
      "icon_account-close", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_account-close.svg",
      ),
    );

    this.iconRegistry.addSvgIcon(
      "icon_account-open", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_account-open.svg",
      ),
    );
  }

  ngOnInit() {
    //Admin の一覧で表示を制御する
    this.displayedColumns = ["name", "status", "assignedDate"];

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.sectionGroupId = params.get("sectionGroupId");
      this.listAdmins(this.sectionGroupId);

      this.hasAllSectionGroupManagement =
        this.common.hasAllSectionGroupManagement();
      this.hasSectionGroupAdminManagement =
        this.common.hasSectionGroupAdminManagement(this.sectionGroupId);
    });

    this.common.parametersUpdated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((noData) => {});
  }

  ngAfterViewInit() {}

  applyFilter(event?: Event) {
    if (event) {
      this.filter = (event.target as HTMLInputElement).value;
    }
    const filterValue = this.filter.trim().toLowerCase();
    this.dataSource.filter = filterValue;
  }

  // M3 Expandable Search methods
  expandSearch(): void {
    this.searchExpanded.set(true);
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 50);
  }

  collapseSearch(): void {
    if (!this.filter.trim()) {
      this.searchExpanded.set(false);
    }
  }

  onSearchBlur(): void {
    this.collapseSearch();
  }

  clearSearch(event: Event): void {
    event.stopPropagation();
    this.filter = "";
    this.applyFilter();
  }

  inviteAdmin() {
    const dialogRef = this.dialog.open(SectionGroupAdminDetailsComponent, {
      width: "740px",
      disableClose: false,

      data: {
        title: this.i18n.dialog.confirmTitle,
        message: this.i18n.dialog.confirmMessage,
        sectionGroupId: this.sectionGroupId,
        mode: SectionGroupAdminDetailMode.invite,
      },
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {});
  }

  createAdmin() {
    const dialogRef = this.dialog.open(SectionGroupAdminDetailsComponent, {
      width: "740px",
      disableClose: false,

      data: {
        title: this.i18n.dialog.confirmTitle,
        message: this.i18n.dialog.confirmMessage,
        sectionGroupId: this.sectionGroupId,
        mode: SectionGroupAdminDetailMode.create,
      },
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result?.needRefresh) {
        this.listAdmins(this.sectionGroupId);
      }
    });
  }

  openAdminDetailsDialog(res): void {

    const dialogRef = this.dialog.open(SectionGroupAdminDetailsComponent, {
      width: "740px",
      disableClose: false,

      data: {
        title: this.i18n.dialog.confirmTitle,
        message: this.i18n.dialog.confirmMessage,
        sectionGroupId: this.sectionGroupId,
        adminData: res,
        mode: SectionGroupAdminDetailMode.disp,
      },
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result?.needRefresh) {
        this.listAdmins(this.sectionGroupId);
      }
    });
  }

  openStaffRolesDialog(refid): void {
    // const dialogRef = this.dialog.open(StaffRolesComponent, {
    //   width: '740px',
    //   disableClose: false,
    //   data: { title: $localize`ダイアログ確認`, message: $localize`ダイアログが表示されます`, id: refid},
    // });
    // dialogRef.afterClosed().subscribe(result => {
    // });
  }

  openStaffActivationDialog(refid): void {
    // const dialogRef = this.dialog.open(StaffActivationComponent, {
    //   width: '740px',
    //   disableClose: false,
    //   data: { title: $localize`ダイアログ確認`, message: $localize`ダイアログが表示されます`, id: refid},
    // });
    // dialogRef.afterClosed().subscribe(result => {
    // });
  }

  refreshData(): void {
    this.listAdmins(this.sectionGroupId);
  }

  listAdmins(sectionGroupId) {
    this.isLoading.set(true);

    this.listSectionGroupAdminsService
      .listSectionGroupAdmins(sectionGroupId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          this.admins = [];
          for (let i = 0; i < (res as any).length; i++) {
            let data = (res as any)[i];

            let userId = data.user?.userId;
            let firstName = data.user?.firstName;
            let lastName = data.user?.lastName;
            let userName = lastName + " " + firstName;
            let roles = data.assignment?.roles;
            let birthDate = data.user?.birthDate;
            let sex = data.user?.sex;
            let assignedData = data.assignment?.commonInfo?.createdAt;
            let lastMofifiedAt = data.assignment?.commonInfo?.lastModifiedAt;
            let lastModifiedBy = data.assignment?.commonInfo?.lastModifiedBy;

            let admin: AdminData = {
              sectionGroupId: data.assignment?.sectionGroupId,
              userId: data.user.userId,

              signInName: null,
              password: null,
              email: null,

              birthDate: birthDate,
              sex: sex,

              lastName: lastName,
              firstName: firstName,
              name: userName,

              roles: roles,

              status: data.assignment?.status?.toUpperCase(),
              assignedDate: dayjs(assignedData).isValid()
                ? dayjs(assignedData).format("YYYY/MM/DD")
                : null,

              lastModifiedAt: dayjs(lastMofifiedAt).isValid()
                ? dayjs(lastMofifiedAt).format("YYYY/MM/DD")
                : null,
              lastModifiedBy: lastModifiedBy,
            };

            if (userId === this.loginUserId) {
              this.admins.unshift(admin);
            } else {
              this.admins.push(admin);
            }
          }

          this.dataSource = new MatTableDataSource(this.admins);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;

          this.isLoading.set(false);
        },
        (error) => {
          this.isLoading.set(false);
        },
      );
  }
}
